import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Truck, Snowflake, Gauge, Wrench, Activity, CheckCircle2, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ServiceEnquiry from '../components/ServiceEnquiry';
import StatCounter from '../components/StatCounter';
import { COMPANY, CONTACT, HERO_IMAGE, WHY_US, SERVICES, DIVISIONS, SHIPPING_PARTNERS, OEM_PARTNERS, GALLERY } from '../mock';

const iconFor = (title) => {
  const t = title.toLowerCase();
  if (t.includes('pti') || t.includes('inspection')) return ShieldCheck;
  if (t.includes('logistic')) return Truck;
  if (t.includes('cold room')) return Snowflake;
  if (t.includes('rrms') || t.includes('monitor')) return Gauge;
  if (t.includes('repair')) return Wrench;
  return Activity;
};

const heroStagger = { hidden: { opacity: 0, y: 26 }, show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.65, delay: i * 0.14, ease: 'easeOut' } }) };

export default function Home() {
  return (
    <div>
      <Seo title="Fixed Group — Reefer Container Repair & Cold Chain Logistics Malaysia" description="Malaysia's trusted specialists in reefer container repair, PTI, cold-chain haulage and cold room solutions since 1999. 24/7 support — Klang & Kota Kinabalu." />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_IMAGE} alt="Reefer containers" className="w-full h-full object-cover hero-zoom" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative fx-container py-16 md:py-24 text-white">
          <div className="max-w-3xl">
            <motion.div variants={heroStagger} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full fx-glass text-xs tracking-widest uppercase text-emerald-300">
              Since {COMPANY.established} • Malaysia
            </motion.div>
            <motion.h1 variants={heroStagger} initial="hidden" animate="show" custom={1}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              Reefer Repairs.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-sky-400">Cold Chain</span> Solutions.
            </motion.h1>
            <motion.p variants={heroStagger} initial="hidden" animate="show" custom={2} className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              Container repair, refrigerated transport and cold room solutions in Malaysia. Supporting your operations since 1999.
            </motion.p>
            <motion.div variants={heroStagger} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap gap-3">
              <Button asChild data-testid="hero-services-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-base shadow-xl shadow-emerald-500/30"><Link to="/services">Explore Services <ArrowRight className="ml-2" size={18} /></Link></Button>
              <Button asChild data-testid="hero-contact-btn" variant="outline" className="fx-glass border-white/30 text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-base"><Link to="/contact">Request a Quote</Link></Button>
            </motion.div>
            <motion.a variants={heroStagger} initial="hidden" animate="show" custom={4} href={`tel:${CONTACT.phones[1].tel}`} data-testid="hero-phone-link"
              className="mt-8 inline-flex items-center gap-3 text-sm text-slate-300 hover:text-emerald-300">
              <span className="w-10 h-10 rounded-full fx-glass flex items-center justify-center"><Phone size={16} className="text-emerald-400" /></span>
              24/7 emergency support — {CONTACT.phones[1].display}
            </motion.a>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative -mb-8 md:-mb-12 z-10">
          <div className="fx-container">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5, ease: 'easeOut' }}
              className="grid grid-cols-2 md:grid-cols-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 overflow-hidden" data-testid="stats-bar">
              {COMPANY.stats.map((s, i) => (
                <div key={i} className="p-6 md:p-8 border-r last:border-r-0 border-slate-100 text-center group hover:bg-emerald-50/50 transition-colors duration-300">
                  <div className="text-3xl md:text-4xl font-black text-slate-900 font-display"><StatCounter value={s.value} /></div>
                  <div className="mt-2 text-xs md:text-sm font-medium leading-relaxed uppercase tracking-wider text-slate-700">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24">
        <div className="fx-container">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold">What we do</span>
              <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-2">Our Core Services</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-emerald-700 font-semibold hover:text-emerald-800 hover:gap-3 transition-all" data-testid="see-all-services-link">See all services <ArrowRight size={16} /></Link>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.filter((_, index) => [0, 1, 4, 5].includes(index)).map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="fx-card overflow-hidden group h-full">
                  <div className="h-40 overflow-hidden">
                    <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-slate-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <ServiceEnquiry service={s.title} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="pt-20 md:pt-28 pb-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-32 w-[420px] h-[420px] rounded-full bg-emerald-100/50 blur-3xl pointer-events-none" />
        <div className="fx-container grid lg:grid-cols-2 gap-14 items-center relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Fixed Group Overview</div>
            <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-4">Cold chain expertise that ports and logistics teams rely on.</h2>
            <p className="mt-6 text-slate-600 leading-relaxed">{COMPANY.overview}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {['24/7 emergency response team', 'GPS-tracked cold chain fleet', 'OEM-certified technicians', 'Pioneer of RRMS smart monitoring'].map((p, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-100 p-3 hover:border-emerald-200 hover:bg-emerald-50/50 transition-colors"><CheckCircle2 className="text-emerald-500 mt-0.5 flex-shrink-0" size={18} /><span className="text-slate-700 text-sm font-medium">{p}</span></li>
              ))}
            </ul>
            <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-emerald-700 font-semibold hover:text-emerald-800 hover:gap-3 transition-all" data-testid="learn-more-link">Learn more about us <ArrowRight size={16} /></Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-emerald-200/40 to-sky-200/40 blur-xl pointer-events-none" />
            <img src="https://images.pexels.com/photos/30748602/pexels-photo-30748602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Cold chain operations" className="relative rounded-2xl w-full h-[420px] object-cover shadow-2xl shadow-slate-900/15" />
            <div className="absolute -top-5 -right-4 bg-slate-900 text-white px-4 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest shadow-xl hidden md:block">Since {COMPANY.established}</div>
            <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-xl p-5 rounded-2xl shadow-xl border border-emerald-100 hidden md:block">
              <div className="text-4xl font-black text-emerald-600 font-display"><StatCounter value="25+" /></div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Years serving cold chain</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-slate-50">
        <div className="fx-container">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold">Why Choose Us</span>
            <h2 className="section-title center text-3xl md:text-4xl font-extrabold mt-2">Built for the demands of modern cold chain</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_US.map((w, i) => {
              const Icon = iconFor(w.title);
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="fx-card p-7 h-full group">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 transition-colors duration-300">
                      <Icon className="text-emerald-600 group-hover:text-white transition-colors duration-300" size={22} />
                    </div>
                    <h3 className="mt-4 font-bold text-slate-900">{w.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-600/10 blur-3xl" />
        <div className="fx-container relative">
          <Reveal className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold">Our Companies</span>
            <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-2 text-white">A group built for every stage of the cold chain</h2>
            <p className="mt-4 text-slate-300">Three specialized entities working together — from container repair to on-road temperature-controlled transport.</p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {DIVISIONS.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.12}>
                <Link to={`/divisions/${d.slug}`} data-testid={`division-card-${d.slug}`} className="block group rounded-2xl overflow-hidden fx-glass hover:border-emerald-500/60 transition-colors duration-300 h-full">
                  <div className="h-44 overflow-hidden">
                    <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-emerald-400">Est. {d.established}</div>
                    <h3 className="mt-2 text-lg font-bold">{d.name}</h3>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{d.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-emerald-400 text-sm font-semibold group-hover:gap-3 transition-all">View division <ArrowRight size={14} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Existing operational photos: no unverified project outcomes. */}
      <section className="py-16">
        <div className="fx-container">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold">Our team at work</h2>
          <p className="mt-4 text-slate-600">A closer look at our reefer servicing and on-site operations.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {GALLERY.filter(item => item.image.startsWith('/gallery/')).slice(0, 3).map(item => (
              <figure key={item.image} className="fx-card overflow-hidden">
                <img src={item.image} alt={item.title} loading="lazy" className="w-full h-56 object-cover" />
                <figcaption className="p-5 text-sm font-medium text-slate-700">{item.title}</figcaption>
              </figure>
            ))}
          </div>
          <Link to="/gallery" className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-semibold">View our gallery <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 border-y border-slate-200 bg-white overflow-hidden">
        <div className="fx-container">
          <Reveal className="text-center">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Trusted by leading shipping lines & OEM partners</span>
          </Reveal>
          <div className="mt-10 overflow-hidden" data-testid="partner-marquee">
            <div className="marquee-track items-center">
              {[...SHIPPING_PARTNERS, ...OEM_PARTNERS, ...SHIPPING_PARTNERS].map((p, i) => (
                <div key={i} className="logo-item">
                  <BrandLogo name={p.name} logo={p.logo} className="h-14 md:h-16 w-auto max-w-[190px]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="fx-container">
          <Reveal>
            <div className="rounded-3xl overflow-hidden relative bg-gradient-to-br from-emerald-600 to-sky-600 p-10 md:p-16 text-white shadow-2xl shadow-emerald-600/25">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-2xl" />
              <div className="relative grid md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-2">
                  <h3 className="text-2xl md:text-3xl font-extrabold">Need cold chain expertise you can count on?</h3>
                  <p className="mt-3 text-white/90">From port-side PTI to nationwide reefer haulage, our team is ready to support your operations 24/7.</p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85">
                    {CONTACT.phones.map((p) => (
                      <a key={p.tel} href={`tel:${p.tel}`} className="inline-flex items-center gap-2 hover:text-white"><Phone size={14} /> {p.display}</a>
                    ))}
                  </div>
                </div>
                <div className="md:text-right">
                  <Button asChild data-testid="cta-contact-btn" className="bg-white text-emerald-700 hover:bg-slate-100 rounded-full px-8 py-6 text-base font-semibold shadow-xl"><Link to="/contact">Request a Quote <ArrowRight className="ml-2" size={18} /></Link></Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
